import * as React from "react";
import { X, Trash2 } from "lucide-react";
import {
  FileUpload,
  FileUploadDropzone,
  FileUploadTrigger,
  FileUploadList,
  FileUploadItem,
  FileUploadItemPreview,
  FileUploadItemProgress,
  FileUploadItemDelete,
  useFileUpload,
  type FileUploadProps,
} from "@/shared/ui/components/ui/file-upload";
import { Button } from "./Button";
import { Label } from "@/shared/ui/components/ui/label";
import { cn } from "@/shared/lib/utils";
import { useTranslation } from "react-i18next";

function formatBytes(bytes: number) {
  if (bytes === 0) return "0 B";
  const sizes = ["B", "KB", "MB", "GB", "TB"];
  const i = Math.floor(Math.log(bytes) / Math.log(1024));
  return `${(bytes / 1024 ** i).toFixed(i ? 1 : 0)} ${sizes[i]}`;
}

interface FileUploadItemWrapperProps {
  file: File;
}

function FileUploadItemWrapper({ file }: FileUploadItemWrapperProps) {
  const { t } = useTranslation();
  const fileState = useFileUpload((state) => state.files.get(file));
  const [imagePreview, setImagePreview] = React.useState<string | null>(null);

  const isImage = file.type.startsWith("image/");

  React.useEffect(() => {
    if (isImage) {
      const url = URL.createObjectURL(file);
      setImagePreview(url);
      return () => URL.revokeObjectURL(url);
    }
  }, [file, isImage]);

  const handleImageClick = () => {
    if (imagePreview) {
      window.open(imagePreview, "_blank");
    }
  };

  if (!fileState) return null;

  const { status, progress, error } = fileState;
  const isUploading = status === "uploading";
  const isSuccess = status === "success";
  const isError = status === "error" || !!error;

  if (isImage && imagePreview) {
    return (
      <FileUploadItem
        value={file}
        className="relative inline-block w-24 h-24 rounded-xl overflow-hidden border-0 p-0"
      >
        <img
          src={imagePreview}
          alt={file.name}
          className="w-full h-full object-cover cursor-pointer"
          onClick={handleImageClick}
        />
        <FileUploadItemDelete className="absolute top-1 right-1 p-1 bg-white rounded-full shadow hover:bg-grey-100 cursor-pointer">
          <X className="w-3 h-3 text-grey-900" />
        </FileUploadItemDelete>
      </FileUploadItem>
    );
  }

  return (
    <FileUploadItem
      value={file}
      className={cn(
        "flex items-center gap-3 rounded-lg border border-grey-100 bg-white p-3",
        isError && "border-error-700 bg-error-bg"
      )}
    >
      <div className="flex-shrink-0">
        <FileUploadItemPreview />
      </div>

      <div className="flex-1 min-w-0">
        <div className="flex flex-col gap-0.5">
          <span className="text-xs t-medium text-grey-900 truncate">
            {file.name}
          </span>
          <span className="text-xs text-grey-500">
            {formatBytes(file.size)}
          </span>
          {isError && error && (
            <span className="text-xs text-error-700 mt-1">
              {t("common.fileFailedToUpload")}
            </span>
          )}
        </div>
        {isUploading && (
          <div className="mt-2 w-full">
            <FileUploadItemProgress forceMount className="h-1.5 w-full" />
          </div>
        )}
      </div>

      {isUploading && (
        <span className="text-xs font-bold text-grey-900 whitespace-nowrap flex-shrink-0">
          {Math.round(progress)}%
        </span>
      )}

      <div className="flex items-center gap-2 flex-shrink-0">
        {isUploading && (
          <FileUploadItemDelete className="p-1 hover:bg-grey-100 rounded cursor-pointer">
            <X className="w-4 h-4 text-grey-900" />
          </FileUploadItemDelete>
        )}
        <FileUploadItemDelete className="p-1 hover:bg-grey-100 rounded cursor-pointer">
          <Trash2 className="w-4 h-4 text-grey-900" />
        </FileUploadItemDelete>
      </div>
    </FileUploadItem>
  );
}

export interface SimpleFileUploaderProps
  extends Omit<FileUploadProps, "children"> {
  label?: string;
  buttonLabel?: string;
  dropzoneText?: string;
  hintText?: string;
  showFileCount?: boolean;
  showError?: boolean;
  errorMessage?: string;
  className?: string;
  buttonClassName?: string;
  dropzoneTextClassName?: string;
  disabled?: boolean;
}

const SimpleFileUploader = React.forwardRef<
  HTMLDivElement,
  SimpleFileUploaderProps
>(
  (
    {
      label = "Upload file",
      buttonLabel = "Choose file",
      dropzoneText = "Drop files here to upload",
      hintText,
      showFileCount = true,
      showError = true,
      buttonClassName,
      dropzoneTextClassName,
      errorMessage,
      className,
      disabled = false,
      value = [],
      onValueChange,
      ...props
    },
    ref
  ) => {
    const files = value || [];

    return (
      <FileUpload
        ref={ref}
        value={value}
        onValueChange={onValueChange}
        className={cn("space-y-3", className, disabled && "cursor-not-allowed")}
        disabled={disabled}
        {...props}
      >
        <div className="space-y-2 rtl:text-right ">
          {label && (
            <Label className={`text-grey-900 text-sm font-medium ${disabled ? "text-grey-200" : ""}`}>
              {label}
            </Label>
          )}

          <FileUploadDropzone
            className={cn(
              "border border-double border-grey-100 rounded-lg overflow-hidden p-0 h-auto",
              "bg-transparent hover:bg-transparent",
              "data-dragging:border-brand-green data-dragging:bg-green-50/30",
              disabled && " cursor-not-allowed hover:border-grey-200 ",
              Boolean(errorMessage) &&
              "border-error-700 !bg-error-bg !text-error-700  hover:border-error-700"
            )}
          >
            <div className="flex  rtl:flex-row-reverse w-full ">
              <div className="flex-shrink-0">
                <FileUploadTrigger asChild>
                  <Button
                    variant="black-grey"
                    className={cn(
                      "rounded-none rounded-l-lg border-0 px-5 py-3 h-full",
                      "text-sm font-medium text-white",
                      buttonClassName,
                      disabled &&
                      " cursor-not-allowed bg-grey-200 hover:bg-grey-200 border-grey-200 hover:border-grey-200",
                      Boolean(errorMessage) && "bg-error-700 hover:bg-error-700"
                    )}
                    disabled={disabled}
                  >
                    {buttonLabel}
                  </Button>
                </FileUploadTrigger>
              </div>

              <div
                className={cn(
                  "flex-1 bg-white  px-5 py-3 h-full border-l border-grey-100 rounded-r-lg ",
                  disabled && "cursor-not-allowed",
                  dropzoneTextClassName,
                  Boolean(errorMessage) && "bg-error-bg"
                )}
              >
                <span
                  className={cn(
                    "text-sm font-medium text-grey-300 ",

                    disabled && "cursor-not-allowed bg-grey-50 text-grey-200",
                    Boolean(errorMessage) &&
                    "text-error-700 hover:border-error-700 "
                  )}
                >
                  {dropzoneText}
                </span>
              </div>
            </div>
          </FileUploadDropzone>

          {showError && errorMessage && (
          <p className="text-xs text-error-700 mt-1">{errorMessage}</p>
        )}
          {hintText && <p className={`text-xs text-grey-500 ${disabled ? "!text-grey-200" : ""}`}>{hintText}</p>}
        </div>

        <FileUploadList className="space-y-2">
          {files.map((file) => (
            <FileUploadItemWrapper key={file.name} file={file} />
          ))}
        </FileUploadList>

    
        {showFileCount && (
          <div className="flex items-center gap-3">
            <span className={`text-xs text-grey-400 ${disabled ? "!text-grey-200" : ""}`}>
              {files.length} file{files.length === 1 ? "" : "s"} selected
            </span>
          </div>
        )}

      </FileUpload>
    );
  }
);

SimpleFileUploader.displayName = "SimpleFileUploader";

export { SimpleFileUploader };
